import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import service from "../../../../services/rejectionApprovalService";
import "./RejectionApproval.scss";
const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })
    : "?";
const Reasons = ({ rows }) => (
  <ul className="approval-reasons">
    {(rows || []).map((row, i) => (
      <li key={i}>
        {row.reasonCode || "?"} {row.reasonName}{" "}
        <strong>- {row.quantity}</strong>
      </li>
    ))}
  </ul>
);
export default function RejectionApproval({ report = false }) {
  const [filters, setFilters] = useState({
    jobcard: "",
    status: "",
    from: "",
    to: "",
    page: 1,
  });
  const [draft, setDraft] = useState({
    jobcard: "",
    status: "",
    from: "",
    to: "",
  });
  const [data, setData] = useState({ rows: [], total: 0, pageSize: 25 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [decision, setDecision] = useState(null);
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);
  const [decisionError, setDecisionError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const reload = useCallback(() => setRefresh((value) => value + 1), []);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    service
      .list(filters, report, controller.signal)
      .then(setData)
      .catch((err) => {
        if (err.name !== "AbortError") {
          setError(err.message);
          setData({ rows: [], total: 0, pageSize: 25 });
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [filters, report, refresh]);
  const submit = async (event) => {
    event.preventDefault();
    if (saving || !comments.trim()) return;
    setSaving(true);
    setDecisionError("");
    try {
      await service.decide(decision.row._id, decision.action, comments.trim());
      setNotice(
        `${decision.row.jobcard}: ${decision.action === "Approve" ? "rejection approved" : "rejection revoked; only this request's quantities were reversed"}.`,
      );
      setDecision(null);
      setComments("");
      reload();
    } catch (err) {
      setDecisionError(err.message);
    } finally {
      setSaving(false);
    }
  };
  const openDecision = (row, action) => {
    setDecision({ row, action });
    setComments("");
    setDecisionError("");
  };
  const pages = Math.max(1, Math.ceil(data.total / data.pageSize));
  return (
    <main id="main" className="main rejection-approval-page p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3>{report ? "Approval Report" : "Rejection Approval"}</h3>
          <p className="text-muted mb-0">
            {report
              ? "Approved and revoked rejection requests, with decision comments and quantity details."
              : "Review rejection quantities before the jobcard can be closed. A Supervisor must make the decision."}
          </p>
        </div>
        <Button variant="outline-primary" onClick={reload} disabled={loading}>
          Refresh
        </Button>
      </div>
      <hr />
      {error && (
        <Alert variant="danger" role="alert" className="p-3">
          {error}
        </Alert>
      )}
      {notice && (
        <Alert variant="success" dismissible onClose={() => setNotice("")} className="p-3">
          {notice}
        </Alert>
      )}
      <Card className="mb-3">
        <Card.Body>
          <Form
            className="approval-filters"
            onSubmit={(event) => {
              event.preventDefault();
              setFilters({ ...draft, page: 1 });
            }}
          >
            <Form.Group controlId="approval-jobcard">
              <Form.Label>Jobcard</Form.Label>
              <Form.Control
                value={draft.jobcard}
                maxLength={100}
                onChange={(e) =>
                  setDraft({ ...draft, jobcard: e.target.value })
                }
                placeholder="Search jobcard"
              />
            </Form.Group>
            {report && (
              <Form.Group controlId="approval-status">
                <Form.Label>Decision</Form.Label>
                <Form.Select
                  value={draft.status}
                  onChange={(e) =>
                    setDraft({ ...draft, status: e.target.value })
                  }
                >
                  <option value="">All decisions</option>
                  <option value="Approved">Approve</option>
                  <option value="Revoked">Revoke</option>
                </Form.Select>
              </Form.Group>
            )}
            <Form.Group controlId="approval-from">
              <Form.Label>{report ? "Decision" : "Requested"} from</Form.Label>
              <Form.Control
                type="date"
                value={draft.from}
                onChange={(e) => setDraft({ ...draft, from: e.target.value })}
              />
            </Form.Group>
            <Form.Group controlId="approval-to">
              <Form.Label>To (inclusive)</Form.Label>
              <Form.Control
                type="date"
                min={draft.from}
                value={draft.to}
                onChange={(e) => setDraft({ ...draft, to: e.target.value })}
              />
            </Form.Group>
            <Button type="submit" disabled={loading}>
              Apply
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => {
                const empty = { jobcard: "", status: "", from: "", to: "" };
                setDraft(empty);
                setFilters({ ...empty, page: 1 });
              }}
            >
              Clear
            </Button>
          </Form>
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between mb-3">
            <strong>
              {data.total} {report ? "decisions" : "pending requests"}
            </strong>
            {/* <span className="text-muted">Times shown in IST</span> */}
          </div>
          {loading ? (
            <div role="status" className="p-4 text-center">
              <Spinner animation="border" size="sm" /> Loading?
            </div>
          ) : (
            <Table responsive hover className="align-middle">
              <thead>
                <tr>
                  <th>Jobcard / size</th>
                  <th>Department / process</th>
                  <th>Rejection ACTUAL / limit</th>
                  <th>Rejection reasons</th>
                  <th>Requested by / date</th>
                  <th>{report ? "Decision" : "Action"}</th>
                  {report && <th>Comments / reset details</th>}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row._id}>
                    <td>
                      <strong>{row.jobcard}</strong>
                      <small className="d-block text-muted">
                        {row.sizeCode} {row.sizeName}
                      </small>
                    </td>
                    <td>
                      {row.deptName}
                      <small className="d-block">
                        Process {row.order ?? "-"}
                      </small>
                    </td>
                    <td>
                      <strong>{row.actualQuantity}</strong> / {row.threshold}
                    </td>
                    <td>
                      <Reasons rows={row.reasons} />
                    </td>
                    <td>
                      {row.requestedByName} - {row.requestedBy}
                      {/* <small className="d-block text-muted">
                        
                      </small> */}
                      <small className="d-block">
                        {formatDate(row.requestedAt)}
                      </small>
                    </td>
                    <td>
                      {report ? (
                        <>
                          <Badge
                            bg={
                              row.status === "Approved" ? "success" : "danger"
                            }
                          >
                            {row.status}
                          </Badge>
                          <div>{row.decidedByName} - {row.decidedBy}</div>
                          {/* <small></small> */}
                          <small className="d-block">
                            {formatDate(row.decidedAt)}
                          </small>
                        </>
                      ) : (
                        <>
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="success"
                              disabled={!row.canDecide}
                              onClick={() => openDecision(row, "Approve")}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              disabled={!row.canDecide}
                              onClick={() => openDecision(row, "Revoke")}
                            >
                              Revoke
                            </Button>
                          </div>
                          {!row.canDecide && (
                            <small className="d-block mt-2 text-muted">
                              Requires a Supervisor approval.
                            </small>
                          )}
                        </>
                      )}
                    </td>
                    {report && (
                      <td className="approval-comments">
                        {row.comments}
                        {row.status === "Revoked" && (
                          <details className="mt-2">
                            <summary>
                              Reversed: {row.resetQuantity} rejected pieces
                            </summary>
                            <Reasons rows={row.resetReasons} />
                          </details>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {!data.rows.length && (
                  <tr>
                    <td
                      colSpan={report ? 7 : 6}
                      className="p-4 text-center text-muted"
                    >
                      {report
                        ? "No approval decisions match these filters."
                        : "No pending rejection approvals match these filters."}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
          <div className="d-flex justify-content-end align-items-center gap-3">
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={loading || filters.page <= 1}
              onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
            >
              Previous
            </Button>
            <span>
              Page {filters.page} of {pages}
            </span>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={loading || filters.page >= pages}
              onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
            >
              Next
            </Button>
          </div>
        </Card.Body>
      </Card>
      <Modal
        show={!!decision}
        onHide={() => {
          if (!saving) setDecision(null);
        }}
        centered
        backdrop="static"
      >
        <Form onSubmit={submit}>
          <Modal.Header closeButton={!saving}>
            <Modal.Title>
              {decision?.action} rejection ? {decision?.row.jobcard}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {decisionError && <Alert variant="danger" className="p-3">
              {decisionError}
            </Alert>}
            <p>
              {decision?.action === "Revoke"
                ? "Only the rejection quantities in this approval request will be reversed. Previously approved rejection and other records remain unchanged. The operator can enter the corrected quantities again."
                : "The confirmed rejection will be accepted. The jobcard can be closed once its quantity checks are satisfied."}
            </p>
            <Form.Group controlId="approval-comments">
              <Form.Label>Comments (required)</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                autoFocus
                required
                maxLength={2000}
                value={comments}
                disabled={saving}
                onChange={(e) => setComments(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              disabled={saving}
              onClick={() => setDecision(null)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant={decision?.action === "Revoke" ? "danger" : "success"}
              disabled={saving || !comments.trim()}
            >
              {saving ? "Saving?" : decision?.action}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </main>
  );
}
